import {makeAutoObservable} from "mobx";
import {DateObservable} from "../../../observables/dateObservable";
import {ObservableList} from "../../../observables/observableList";
import {KeyValue} from "../../../observables/keyValue";
import {
  recurrenceOnce,
  recurrenceDaily,
  recurrenceWeekly,
  recurrenceMonthly,
  recurrenceEndsNever,
  recurrenceEndsOnThisDay
} from "../../../../shared/constants";
import {WeekObservableList} from "../../../observables/weekObservableList";
import {DayObservableList} from "../../../observables/dayObservableList";
import {utcToZonedTime} from "date-fns-tz";

export class EventRecurrenceDetails {
  recurrenceEndDate: DateObservable;
  baseDate?: Date;
  interval: number = 1; // Defaults to 1 because we only support weekly for now.
  private readonly _recurrenceTypes: ObservableList<KeyValue>;
  datePickerSelected?: boolean;
  selectedRecurrenceEndType?: "endsNever" | "endsOnThisDay"; // endsOnThisDay or endsNever
  timeZone: string;
  weekdays: DayObservableList;
  monthWeeks: WeekObservableList;

  constructor(
    timeZone: string,
    recurrenceEndDate?: DateObservable,
    frequency: "once" | "weekly" | "monthly" = recurrenceOnce // TODO: make enum
  ) {
    makeAutoObservable(this);
    this.timeZone = timeZone;
    this.datePickerSelected = false;
    this.selectedRecurrenceEndType = recurrenceEndsNever;
    this.recurrenceEndDate = recurrenceEndDate ?? new DateObservable();

    this._recurrenceTypes = new ObservableList<KeyValue>(
      [
        new KeyValue(recurrenceOnce, "Once"),
        new KeyValue(recurrenceDaily, "Daily"),
        new KeyValue(recurrenceWeekly, "Weekly"),
        new KeyValue(recurrenceMonthly, "Monthly")
      ],
      frequency
    );
    this.monthWeeks = new WeekObservableList();
    this.weekdays = new DayObservableList();
  }

  get isRecurringEvent() {
    return (
      this.frequency === recurrenceDaily ||
      this.frequency === recurrenceWeekly ||
      this.frequency === recurrenceMonthly
    );
  }

  set frequency(frequency: "once" | "daily" | "weekly" | "monthly") {
    this._recurrenceTypes.setSelectedItem(frequency);
  }

  get frequency() {
    return this._recurrenceTypes.selectedItem!.value as
      | "once"
      | "daily"
      | "weekly"
      | "monthly";
  }

  getRecurrenceTypes() {
    return this._recurrenceTypes;
  }

  getRecurrenceEndDate(eventEndDate: Date): string | null {
    const neverEndingEventDate = "9999-01-01";

    if (this.selectedRecurrenceEndType === recurrenceEndsNever) {
      return neverEndingEventDate;
    }

    if (this.selectedRecurrenceEndType === recurrenceEndsOnThisDay) {
      const localTime = utcToZonedTime(
        this.recurrenceEndDate.date!,
        this.timeZone
      );

      const endDate = new Date(
        localTime.getFullYear(),
        localTime.getMonth(),
        localTime.getDate(),
        eventEndDate.getHours(),
        eventEndDate.getMinutes(),
        eventEndDate.getSeconds()
      );

      return endDate.toISOString();
    }

    return null;
  }

  get isValid(): boolean {
    return (
      this.selectedRecurrenceEndType !== recurrenceEndsOnThisDay ||
      this.recurrenceEndDate.isValid
    );
  }

  monthRecurrence(isEditing: boolean): string | undefined | null {
    if (this.frequency === recurrenceWeekly) {
      // CMS requires empty monthRecurrence when creating event and null when updating. This conversion in admin portal is a temporary.
      return isEditing ? null : "";
    }

    if (this.frequency === recurrenceMonthly) {
      return "weeks";
    }

    return null;
  }

  setDaysWeeks(
    eventStartDate: string,
    timezoneCode: string,
    days?: number[],
    weeks?: number[]
  ) {
    if (weeks) {
      this.monthWeeks = new WeekObservableList(
        weeks,
        new Date(eventStartDate),
        timezoneCode
      );
    }
    if (days) {
      this.weekdays = new DayObservableList(
        days,
        new Date(eventStartDate),
        timezoneCode
      );
    }
  }
}
